import { Link } from "react-router-dom";

import LegalPage, {
  proseLinkStyle,
  type LegalSection,
} from "@/components/landing/dcv2/LegalPage";

/**
 * Privacy Policy.
 *
 * The effective date is a literal for the same reason it is in Terms.tsx: a
 * computed date would silently reset to today on every page load, so nobody
 * could tell which version they read. Edit this string when the policy changes.
 */
const EFFECTIVE_DATE = "28 September 2026";

const SECTIONS: LegalSection[] = [
  {
    n: "1",
    title: "Information We Collect",
    blocks: [
      {
        bullets: [
          {
            lead: "Account Information",
            text: "Name, email address, company name, billing details, and login credentials when you create an account.",
          },
          {
            lead: "User Inputs and Content",
            text: "Prompts, text, files, and data you submit to our AI models while using the platform.",
          },
          {
            lead: "Usage and Technical Data",
            text: "Log data, IP address, browser type, device information, and interaction metrics with our application and APIs.",
          },
        ],
      },
    ],
  },
  {
    n: "2",
    title: "How We Use Your Information",
    blocks: [
      { p: "We use the information we collect to:" },
      {
        bullets: [
          "Provide, maintain, secure, and improve the AI2Aim platform and its AI models.",
          "Process payments and manage your subscription.",
          "Send administrative updates, security alerts, and customer support messages.",
          "Analyze platform usage trends to enhance user experience and develop new features.",
          {
            lead: "Note on AI Training",
            text: "We do not use your proprietary User Inputs or generated AI Outputs to train our foundational public models unless you give us explicit, separate permission to do so.",
          },
        ],
      },
    ],
  },
  {
    n: "3",
    title: "Data Sharing and Disclosure",
    blocks: [
      {
        p: "We do not sell your personal data. We may share information strictly with:",
      },
      {
        bullets: [
          {
            lead: "Cloud & Infrastructure Providers",
            text: "Trusted vendors who host our application databases and AI compute clusters.",
          },
          {
            lead: "Payment Processors",
            text: "Secure third-party services to handle financial transactions.",
          },
          {
            lead: "Legal Compliance",
            text: "Authorities if required by law, subpoena, or to protect the safety and rights of AI2Aim and our users.",
          },
        ],
      },
    ],
  },
  {
    n: "4",
    title: "Data Security",
    blocks: [
      {
        p: "We implement robust administrative, technical, and physical security measures, including encryption in transit and at rest, to protect your data. However, no digital transmission over the internet is completely secure.",
      },
    ],
  },
  {
    n: "5",
    title: "Your Data Rights",
    blocks: [
      {
        p: (
          <>
            Depending on your jurisdiction (such as GDPR or CCPA), you may have
            the right to access, correct, export, or delete your personal data
            and account history by contacting us directly. For data we received
            from Facebook or Instagram, see our{" "}
            <Link to="/data-deletion" style={proseLinkStyle}>
              data deletion instructions
            </Link>
            .
          </>
        ),
      },
    ],
  },
  {
    n: "6",
    title: "Contact Us",
    blocks: [
      {
        p: "If you have any questions or concerns regarding these Terms or our Privacy Policy, please reach out to our team at contact@ai2aim.ai.",
      },
    ],
  },
];

export default function Privacy() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      effectiveDate={EFFECTIVE_DATE}
      intro="At AI2Aim, we respect your privacy. This Privacy Policy outlines how we collect, use, process, and secure your information when you use our SaaS platform and AI tools."
      sections={SECTIONS}
    />
  );
}
